import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { extractApiError, unwrapSingle } from "@/lib/api";
import { fetchRbacCatalog } from "@/lib/rbac";

const CRUD_ORDER = ["CREATE", "READ", "UPDATE", "DELETE"];
const SELF_LOCKED_PERMISSION_KEY = "USERS.MANAGE_PERMISSIONS";

function toStringArray(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((item) => typeof item === "string" && item.trim().length > 0);
}

function normalizePermissionPayload(raw = {}) {
  const grantableSet = new Set(toStringArray(raw.grantable));
  const effectiveRaw = toStringArray(raw.effective || raw.permissions || raw.current);
  if (effectiveRaw.some((key) => !grantableSet.has(key))) console.warn("[PermissionsEditor] Server returned effective permission(s) outside grantable set — this may indicate a backend/catalog sync issue:", effectiveRaw.filter((key) => !grantableSet.has(key)));
  const effective = effectiveRaw.filter((key) => grantableSet.has(key));

  return {
    customizable: Boolean(raw.customizable),
    effective,
    granted: toStringArray(raw.granted),
    revoked: toStringArray(raw.revoked),
    grantable: [...grantableSet],
    org_type: raw.org_type || null,
  };
}

function normalizeCatalogPermissions(catalog) {
  return (Array.isArray(catalog?.permissions) ? catalog.permissions : [])
    .map((item) => ({
      key: item?.key || "",
      module: item?.module || "",
      action: String(item?.action || "").toUpperCase(),
      label: item?.label || "",
      group: item?.group || "Other",
    }))
    .filter((item) => item.key && item.module && item.action);
}

function areSetsEqual(left, right) {
  if (left.size !== right.size) return false;
  for (const item of left) {
    if (!right.has(item)) return false;
  }
  return true;
}

function formatTitle(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatOrgType(value) {
  if (!value) return "this organization";
  return formatTitle(value);
}

function CellToggle({ permission, checked, locked, grantableLabel, granted, revoked, onToggle }) {
  if (!permission) {
    return <div className="text-center text-slate-400">—</div>;
  }

  const toneClass = granted
    ? "border-emerald-300 bg-emerald-50"
    : revoked
      ? "border-rose-300 bg-rose-50"
      : "border-slate-200 bg-white";

  return (
    <label
      className={`inline-flex items-center justify-center rounded-md border px-2 py-1.5 ${toneClass} ${
        locked ? "cursor-not-allowed opacity-70" : "cursor-pointer"
      }`}
      title={locked ? `Not available for ${grantableLabel}` : permission.label || permission.key}
    >
      <input
        type="checkbox"
        className="h-4 w-4"
        checked={checked}
        disabled={locked}
        onChange={(e) => onToggle(permission.key, e.target.checked)}
      />
      {locked && <Lock size={12} className="ml-1.5 text-slate-500" />}
    </label>
  );
}

export default function PermissionsEditor({ userId, orgType, currentUserId, isCreate, selectedPermissions = [], onPermissionChange, roleDefaults, grantableSet: externalGrantableSet }) {
  const [catalog, setCatalog] = useState(null);
  const [permissionState, setPermissionState] = useState(null);
  const [selectedSet, setSelectedSet] = useState(new Set());
  const [baselineSet, setBaselineSet] = useState(new Set());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const selectedPermissionsSet = useMemo(() => new Set(toStringArray(selectedPermissions)), [selectedPermissions]);

  const publishSelectedPermissions = useCallback((nextSet) => {
    if (onPermissionChange) {
      onPermissionChange([...nextSet].sort());
    }
  }, [onPermissionChange]);

  const applyServerState = useCallback((payload) => {
    const normalized = normalizePermissionPayload(payload || {});
    const nextSet = new Set(normalized.effective);
    if (userId && currentUserId && userId === currentUserId) {
      nextSet.add(SELF_LOCKED_PERMISSION_KEY);
    }
    setPermissionState({
      ...normalized,
      grantableSet: new Set(normalized.grantable),
    });
    setBaselineSet(nextSet);
    setSelectedSet(nextSet);
    publishSelectedPermissions(nextSet);
  }, [currentUserId, publishSelectedPermissions, userId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const catalogResult = await fetchRbacCatalog();
      setCatalog(catalogResult || { permissions: [], fixed_capabilities: [] });

      if (isCreate) {
        // Create mode: use provided defaults and grantable set
        const normalized = {
          customizable: true,
          effective: roleDefaults || [],
          granted: [],
          revoked: [],
          grantable: Array.from(externalGrantableSet || []),
          org_type: orgType,
        };
        const nextSet = new Set(normalized.effective);
        setPermissionState({
          ...normalized,
          grantableSet: new Set(normalized.grantable),
        });
        setBaselineSet(nextSet);
        setSelectedSet(nextSet);
        publishSelectedPermissions(nextSet);
      } else if (userId) {
        // Edit mode: fetch from server
        const permissionsResponse = await api.get(`/users/${userId}/permissions`);
        applyServerState(unwrapSingle(permissionsResponse) || {});
      }
    } catch (err) {
      const apiErr = extractApiError(err);
      setError(apiErr.message || "Failed to load permissions editor.");
    } finally {
      setLoading(false);
    }
  }, [applyServerState, externalGrantableSet, isCreate, orgType, publishSelectedPermissions, roleDefaults, userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const nextSet = new Set(selectedPermissionsSet);
    if (userId && currentUserId && userId === currentUserId) {
      nextSet.add(SELF_LOCKED_PERMISSION_KEY);
    }
    setSelectedSet((prev) => (areSetsEqual(prev, nextSet) ? prev : nextSet));
  }, [currentUserId, selectedPermissionsSet, userId]);

  useEffect(() => {
    // When roleDefaults or grantableSet change in create mode, update selections and state
    if (isCreate && permissionState) {
      const nextSet = new Set(roleDefaults || []);
      const nextGrantableSet = new Set(Array.from(externalGrantableSet || []));
      
      setPermissionState((prev) => ({
        ...prev,
        grantableSet: nextGrantableSet,
      }));
      setBaselineSet(nextSet);
      setSelectedSet(new Set(nextSet));
    }
  }, [isCreate, roleDefaults, externalGrantableSet]);

  useEffect(() => {
    if (!userId || !currentUserId || userId !== currentUserId) return;
    setBaselineSet((prev) => {
      if (prev.has(SELF_LOCKED_PERMISSION_KEY)) return prev;
      const next = new Set(prev);
      next.add(SELF_LOCKED_PERMISSION_KEY);
      return next;
    });
    setSelectedSet((prev) => {
      if (prev.has(SELF_LOCKED_PERMISSION_KEY)) return prev;
      const next = new Set(prev);
      next.add(SELF_LOCKED_PERMISSION_KEY);
      return next;
    });
  }, [currentUserId, userId]);

  const grouped = useMemo(() => {
    const entries = normalizeCatalogPermissions(catalog);
    const byGroup = new Map();

    entries.forEach((entry) => {
      if (!byGroup.has(entry.group)) {
        byGroup.set(entry.group, {
          groupName: entry.group,
          modules: new Map(),
          actions: new Set(),
        });
      }

      const groupObj = byGroup.get(entry.group);
      groupObj.actions.add(entry.action);

      if (!groupObj.modules.has(entry.module)) {
        groupObj.modules.set(entry.module, {
          module: entry.module,
          map: new Map(),
        });
      }

      groupObj.modules.get(entry.module).map.set(entry.action, entry);
    });

    return [...byGroup.values()].map((groupObj) => {
      const actions = [...groupObj.actions];
      const crudActions = CRUD_ORDER.filter((action) => actions.includes(action));
      const specialActions = actions.filter((action) => !CRUD_ORDER.includes(action));

      return {
        groupName: groupObj.groupName,
        crudActions,
        specialActions,
        modules: [...groupObj.modules.values()].sort((a, b) => a.module.localeCompare(b.module)),
      };
    });
  }, [catalog]);

  const fixedCapabilities = Array.isArray(catalog?.fixed_capabilities) ? catalog.fixed_capabilities : [];
  const isDirty = useMemo(() => !areSetsEqual(selectedSet, baselineSet), [selectedSet, baselineSet]);

  const effectiveOrgType = orgType || permissionState?.org_type || null;
  const grantableLabel = formatOrgType(effectiveOrgType);

  const togglePermission = useCallback(
    (permissionKey, checked) => {
      if (userId && currentUserId && userId === currentUserId && permissionKey === SELF_LOCKED_PERMISSION_KEY) return;
      if (!permissionState?.grantableSet?.has(permissionKey)) return;
      const next = new Set(selectedSet);
      if (checked) next.add(permissionKey);
      else next.delete(permissionKey);
      setSelectedSet(next);
      publishSelectedPermissions(next);
    },
    [currentUserId, permissionState, publishSelectedPermissions, selectedSet, userId],
  );

  const handleSave = useCallback(async () => {
    if (!userId) return;
    setSaveLoading(true);
    setError("");
    try {
      const nextPermissions = [...selectedSet];
      if (userId && currentUserId && userId === currentUserId && !nextPermissions.includes(SELF_LOCKED_PERMISSION_KEY)) {
        nextPermissions.push(SELF_LOCKED_PERMISSION_KEY);
      }
      const response = await api.put(`/users/${userId}/permissions`, {
        permissions: nextPermissions.sort(),
      });
      applyServerState(unwrapSingle(response) || {});
    } catch (err) {
      const apiErr = extractApiError(err);
      const status = err?.response?.status;
      const isValidationError = apiErr.code === "VALIDATION_ERROR" || status === 422;
      const fieldMessages = Array.isArray(apiErr.details?.fields)
        ? apiErr.details.fields
            .map((item) => {
              if (!item || typeof item !== "object") return null;
              const field = String(item.field || "field").trim();
              const msg = String(item.message || "Invalid value").trim();
              return `${field} - ${msg}`;
            })
            .filter(Boolean)
        : [];

      if (isValidationError && fieldMessages.length > 0) {
        const baseMessage = apiErr.message || "Failed to save permissions.";
        setError(`${baseMessage} Invalid permissions: ${fieldMessages.join("; ")}`);
      } else {
        setError(apiErr.message || "Failed to save permissions.");
      }
    } finally {
      setSaveLoading(false);
    }
  }, [userId, currentUserId, selectedSet, applyServerState]);

  const handleReset = useCallback(async () => {
    if (isCreate) {
      const nextSet = new Set(roleDefaults || []);
      setSelectedSet(nextSet);
      publishSelectedPermissions(nextSet);
      return;
    }

    if (!userId) return;
    setResetLoading(true);
    setError("");
    try {
      const response = await api.delete(`/users/${userId}/permissions`);
      applyServerState(unwrapSingle(response) || {});
    } catch (err) {
      const apiErr = extractApiError(err);
      setError(apiErr.message || "Failed to reset permissions.");
    } finally {
      setResetLoading(false);
    }
  }, [applyServerState, isCreate, publishSelectedPermissions, roleDefaults, userId]);

  if (!isCreate && !userId) {
    return <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">User ID is required.</div>;
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-slate-600">
        <Loader2 size={18} className="animate-spin" />
        Loading permissions...
      </div>
    );
  }

  if (error) {
    return <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>;
  }

  if (!permissionState) {
    return null;
  }

  if (permissionState.customizable === false) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        Customer accounts use a fixed role.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-800">Permission Matrix</p>
        {!isCreate && isDirty && (
          <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
            Unsaved changes
          </span>
        )}
      </div>

      {grouped.map((group) => (
        <div key={group.groupName} className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-600">
            {group.groupName}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-white">
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2 text-left">Module</th>
                  {group.crudActions.map((action) => (
                    <th key={action} className="px-3 py-2 text-center">{formatTitle(action)}</th>
                  ))}
                  {group.specialActions.length > 0 && <th className="px-3 py-2 text-center">Special</th>}
                </tr>
              </thead>
              <tbody>
                {group.modules.map((moduleRow) => (
                  <tr key={moduleRow.module} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-4 py-3 font-medium text-slate-800">{formatTitle(moduleRow.module)}</td>

                    {group.crudActions.map((action) => {
                      const permission = moduleRow.map.get(action);
                      const key = permission?.key;
                      const selfLocked = key
                        ? userId && currentUserId && userId === currentUserId && key === SELF_LOCKED_PERMISSION_KEY
                        : false;
                      const locked = key ? selfLocked || !permissionState.grantableSet.has(key) : false;
                      const checked = key ? (selfLocked ? true : selectedSet.has(key)) : false;
                      const granted = key ? checked && !baselineSet.has(key) : false;
                      const revoked = key ? !checked && baselineSet.has(key) : false;

                      return (
                        <td key={`${moduleRow.module}-${action}`} className="px-3 py-2 text-center">
                          <CellToggle
                            permission={permission}
                            checked={checked}
                            locked={locked}
                            grantableLabel={grantableLabel}
                            granted={granted}
                            revoked={revoked}
                            onToggle={togglePermission}
                          />
                        </td>
                      );
                    })}

                    {group.specialActions.length > 0 && (
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap justify-center gap-1.5">
                          {group.specialActions.map((action) => {
                            const permission = moduleRow.map.get(action);
                            if (!permission) {
                              return (
                                <div key={`${moduleRow.module}-${action}`} className="text-slate-400">
                                  —
                                </div>
                              );
                            }

                            const key = permission.key;
                            const selfLocked = userId && currentUserId && userId === currentUserId && key === SELF_LOCKED_PERMISSION_KEY;
                            const locked = selfLocked || !permissionState.grantableSet.has(key);
                            const checked = selfLocked ? true : selectedSet.has(key);
                            const granted = checked && !baselineSet.has(key);
                            const revoked = !checked && baselineSet.has(key);

                            return (
                              <div key={key} className="inline-flex items-center gap-1">
                                <CellToggle
                                  permission={permission}
                                  checked={checked}
                                  locked={locked}
                                  grantableLabel={grantableLabel}
                                  granted={granted}
                                  revoked={revoked}
                                  onToggle={togglePermission}
                                />
                                <span className="text-[10px] font-semibold text-slate-500">{formatTitle(action)}</span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="mb-3 text-sm font-semibold text-slate-800">Fixed capabilities</p>
        {fixedCapabilities.length === 0 ? (
          <p className="text-sm text-slate-500">No fixed capabilities listed.</p>
        ) : (
          <ul className="space-y-2">
            {fixedCapabilities.map((item, index) => (
              <li key={`${item?.label || "cap"}-${index}`} className="rounded-md bg-white p-3 ring-1 ring-slate-200">
                <p className="text-sm font-semibold text-slate-800">{item?.label || "Capability"}</p>
                <p className="mt-1 text-xs text-slate-600">{item?.detail || ""}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={handleReset} disabled={isCreate ? resetLoading : resetLoading || saveLoading}>
          {resetLoading ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Resetting...</> : "Reset to role defaults"}
        </Button>
        {!isCreate && (
          <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={handleSave} disabled={saveLoading || resetLoading || !isDirty}>
            {saveLoading ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving...</> : "Save changes"}
          </Button>
        )}
      </div>
    </div>
  );
}
